import { initClickCallback, initPanCallback } from '@/infrastructure/engine2d/input/panCallback';
import type { Canvas, Point, TPointerEventInfo } from 'fabric';
import type { IFabricImageCustom } from '@/infrastructure/engine2d/types/IFabricImageCustom';
import type { IFabricTextCustom } from '@/infrastructure/engine2d/types/IFabricTextCustom';
import type { ITextboxCustom } from '@/infrastructure/engine2d/types/ITextboxCustom';
import type { TViewportConstraints } from '@/utils/TViewportConstraints';

type TClickedActiveObject = IFabricImageCustom | IFabricTextCustom | ITextboxCustom | undefined;

type TPanControllerParams = {
    canvas: Canvas;
    getClickedRefPoint: () => Point | null;
    getConstraints: () => TViewportConstraints;
    notifyZoom: (zoom: number) => void;
    setClickedActiveObject: (object: TClickedActiveObject) => void;
    setClickedRefPoint: (point: Point | null) => void;
};

// Click + pan callbacks share the clicked-ref-point state, so they
// live together in one pointer-drag lifecycle.
class PanController {
    private readonly _canvas: Canvas;
    private readonly _clickCallback: (opt: TPointerEventInfo) => void;
    private readonly _panCallback: (opt: TPointerEventInfo) => void;

    constructor(params: TPanControllerParams) {
        this._canvas = params.canvas;

        this._clickCallback = initClickCallback(params.canvas, params.setClickedRefPoint, params.setClickedActiveObject);
        this._canvas.on('mouse:down', this._clickCallback);
        this._canvas.on('mouse:up', this._clickCallback);

        this._panCallback = initPanCallback(
            params.canvas,
            params.getClickedRefPoint,
            params.setClickedRefPoint,
            params.notifyZoom,
            params.getConstraints,
        );
        this._canvas.on('mouse:move', this._panCallback);
    }

    getClickCallback(): (opt: TPointerEventInfo) => void {
        return this._clickCallback;
    }

    getPanCallback(): (opt: TPointerEventInfo) => void {
        return this._panCallback;
    }

    dispose(): void {
        this._canvas.off('mouse:down', this._clickCallback);
        this._canvas.off('mouse:up', this._clickCallback);
        this._canvas.off('mouse:move', this._panCallback);
    }
}

export { PanController };
